import React, { useCallback, useEffect, useMemo, useState } from 'react';
import httpClient from '../api/httpClient';
import { BrandLogo } from './LoginPage';

const CONFIG_PLATFORMS = {
  apple: 'MacOS',
  windows: 'Windows',
  phone: 'Phone',
};

function getPingToneClassName(ping, isLoading) {
  if (isLoading) {
    return '';
  }

  const pingValue = Number(ping);
  if (!Number.isFinite(pingValue)) {
    return '';
  }

  return pingValue < 100 ? ' ping-cell_good' : ' ping-cell_warn';
}

function VlessKeysPage({ session, onLogout }) {
  const [servers, setServers] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [pingById, setPingById] = useState({});
  const [pingErrorsById, setPingErrorsById] = useState({});
  const [loadingPingIds, setLoadingPingIds] = useState(() => new Set());
  const [copiedId, setCopiedId] = useState('');
  const [loadingConfigKeys, setLoadingConfigKeys] = useState(() => new Set());
  const [configErrorsById, setConfigErrorsById] = useState({});

  const checkPingForServers = useCallback(async (serverIds, signal) => {
    const ids = [...new Set(serverIds.map((serverId) => String(serverId || '')).filter(Boolean))];
    if (ids.length === 0) {
      return;
    }

    setLoadingPingIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setPingErrorsById((current) => {
      const next = { ...current };
      ids.forEach((id) => delete next[id]);
      return next;
    });

    try {
      const response = await httpClient.post('/api/content/check-ping', { ids }, signal ? { signal } : undefined);
      if (signal?.aborted) {
        return;
      }

      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      const itemsById = new Map(items.map((item) => [String(item.id), item]));

      setPingById((current) => {
        const next = { ...current };
        ids.forEach((id) => {
          if (itemsById.has(id)) {
            next[id] = itemsById.get(id).pingMilliseconds ?? null;
          }
        });
        return next;
      });
      setPingErrorsById((current) => {
        const next = { ...current };
        ids.forEach((id) => {
          if (!itemsById.has(id)) {
            next[id] = 'Не удалось проверить пинг.';
          }
        });
        return next;
      });
    } catch (requestError) {
      if (isCanceledRequest(requestError) || signal?.aborted) {
        return;
      }

      setPingErrorsById((current) => {
        const next = { ...current };
        ids.forEach((id) => {
          next[id] = 'Не удалось проверить пинг.';
        });
        return next;
      });
    } finally {
      if (signal?.aborted) {
        return;
      }

      setLoadingPingIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadServers() {
      setIsLoadingList(true);
      setListError('');

      try {
        const response = await httpClient.get('/api/content', { signal: controller.signal });
        const nextServers = Array.isArray(response.data)
          ? response.data.map(normalizeContentItem).filter((server) => server.id && server.key)
          : [];

        setServers(nextServers);
        setExpandedIds(nextServers[0] ? new Set([nextServers[0].id]) : new Set());
        void checkPingForServers(nextServers.map((server) => server.id), controller.signal);
      } catch (requestError) {
        if (isCanceledRequest(requestError)) {
          return;
        }

        setListError('Не удалось загрузить VLESS ключи.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingList(false);
        }
      }
    }

    loadServers();

    return () => controller.abort();
  }, [checkPingForServers]);

  const expiresAt = useMemo(
    () => new Date(session.expiresAt).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    [session.expiresAt],
  );

  const toggleExpanded = (serverId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(serverId)) {
        next.delete(serverId);
      } else {
        next.add(serverId);
      }
      return next;
    });
  };

  const checkPing = (serverId) => {
    void checkPingForServers([serverId]);
  };

  const checkAllPings = () => {
    void checkPingForServers(servers.map((server) => server.id));
  };

  const copyKey = async (server) => {
    await navigator.clipboard.writeText(server.key);
    setCopiedId(server.id);
    window.setTimeout(() => setCopiedId(''), 1600);
  };

  const downloadConfig = async (server, platformKey) => {
    const platform = CONFIG_PLATFORMS[platformKey];
    if (!platform) {
      return;
    }

    const requestKey = `${server.id}:${platform}`;
    setLoadingConfigKeys((current) => {
      const next = new Set(current);
      next.add(requestKey);
      return next;
    });
    setConfigErrorsById((current) => {
      const next = { ...current };
      delete next[server.id];
      return next;
    });

    try {
      const response = await httpClient.get(
        `/api/content/${encodeURIComponent(server.id)}/config/${encodeURIComponent(platform)}`,
        {
          responseType: 'blob',
          headers: {
            Accept: 'application/yaml,text/yaml,text/plain,*/*',
          },
        },
      );

      const contentType = response.headers['content-type'] || 'application/yaml;charset=utf-8';
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: contentType });
      const fileName = getDownloadFileName(
        response.headers['content-disposition'],
        `notreen-${server.id}-${platform.toLowerCase()}.yaml`,
      );

      saveBlob(blob, fileName);
    } catch (requestError) {
      if (isCanceledRequest(requestError)) {
        return;
      }

      setConfigErrorsById((current) => ({
        ...current,
        [server.id]: 'Не удалось скачать конфиг.',
      }));
    } finally {
      setLoadingConfigKeys((current) => {
        const next = new Set(current);
        next.delete(requestKey);
        return next;
      });
    }
  };

  const saveBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const isAnyPingLoading = loadingPingIds.size > 0;
  const isBulkPingDisabled = isLoadingList || Boolean(listError) || servers.length === 0 || isAnyPingLoading;

  return (
    <main className="keys-page">
      <header className="keys-header">
        <BrandLogo />
        <div className="session-panel">
          <div>
            <span>@{session.telegramUsername}</span>
            <small>{expiresAt}</small>
          </div>
          <button className="logout-button" type="button" onClick={onLogout}>
            Выйти
          </button>
        </div>
      </header>

      <section className="keys-shell" aria-label="VLESS ключи">
        <div className="keys-title-row">
          <div>
            <p className="access-kicker">VLESS ключи</p>
            <h1>notreen.vpn</h1>
          </div>
          <div className="keys-title-actions">
            <button
              className="bulk-ping-button"
              disabled={isBulkPingDisabled}
              type="button"
              aria-label="Проверить пинг всех ключей"
              onClick={checkAllPings}
            >
              <SpeedIcon />
              <span>{isAnyPingLoading ? 'Пинг...' : 'Пинг'}</span>
            </button>
            <span className="keys-count">{formatKeysCount(servers.length, isLoadingList, listError)}</span>
          </div>
        </div>

        <div className="keys-list">
          {isLoadingList && <div className="keys-state">Загружаем ключи...</div>}
          {!isLoadingList && listError && <div className="keys-state keys-state_error">{listError}</div>}
          {!isLoadingList && !listError && servers.length === 0 && (
            <div className="keys-state">Активных VLESS ключей нет.</div>
          )}

          {!isLoadingList && !listError && servers.map((server) => {
            const isExpanded = expandedIds.has(server.id);
            const isPingLoading = loadingPingIds.has(server.id);
            const ping = pingById[server.id];
            const pingLabel = isPingLoading ? '...' : ping ?? '-';
            const pingToneClassName = getPingToneClassName(ping, isPingLoading);
            const configError = configErrorsById[server.id];
            const isAppleConfigLoading = loadingConfigKeys.has(`${server.id}:${CONFIG_PLATFORMS.apple}`);
            const isWindowsConfigLoading = loadingConfigKeys.has(`${server.id}:${CONFIG_PLATFORMS.windows}`);
            const isPhoneConfigLoading = loadingConfigKeys.has(`${server.id}:${CONFIG_PLATFORMS.phone}`);

            return (
              <article className={`key-card ${isExpanded ? 'key-card_expanded' : ''}`} key={server.id}>
                <div className="key-card-main">
                  <div className="country-cell">
                    {server.flagUrl ? (
                      <img src={server.flagUrl} alt={`Флаг: ${server.country}`} />
                    ) : (
                      <span className="flag-placeholder" aria-hidden="true" />
                    )}
                    <div>
                      <h2>{server.country}</h2>
                      <p>{server.city || '-'}</p>
                    </div>
                  </div>

                  <div className={`ping-cell${pingToneClassName}`}>
                    <span>{pingLabel}</span>
                    <small>ms</small>
                  </div>

                  <div className="key-actions">
                    <button
                      className="icon-text-button"
                      disabled={isPingLoading}
                      type="button"
                      onClick={() => checkPing(server.id)}
                    >
                      <SpeedIcon />
                      <span>Пинг</span>
                    </button>
                    <button
                      className="icon-button"
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Свернуть ключ' : 'Раскрыть ключ'}
                      onClick={() => toggleExpanded(server.id)}
                    >
                      <ChevronIcon isExpanded={isExpanded} />
                    </button>
                  </div>
                </div>

                {pingErrorsById[server.id] && <p className="ping-error">{pingErrorsById[server.id]}</p>}

                {isExpanded && (
                  <div className="key-details">
                    <label className="key-input">
                      <span>VLESS</span>
                      <input readOnly value={server.key} />
                    </label>
                    <button className="copy-button" type="button" onClick={() => copyKey(server)}>
                      {copiedId === server.id ? 'Скопировано' : 'Копировать'}
                    </button>
                    <div className="download-actions" aria-label="Скачать конфиг">
                      <button
                        type="button"
                        aria-label="Скачать конфиг для Apple"
                        disabled={isAppleConfigLoading}
                        onClick={() => downloadConfig(server, 'apple')}
                      >
                        <AppleIcon />
                      </button>
                      <button
                        type="button"
                        aria-label="Скачать конфиг для Windows"
                        disabled={isWindowsConfigLoading}
                        onClick={() => downloadConfig(server, 'windows')}
                      >
                        <WindowsIcon />
                      </button>
                      <button
                        type="button"
                        aria-label="Скачать конфиг для телефона"
                        disabled={isPhoneConfigLoading}
                        onClick={() => downloadConfig(server, 'phone')}
                      >
                        <PhoneIcon />
                      </button>
                    </div>
                    {configError && <p className="config-error">{configError}</p>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function normalizeContentItem(item) {
  return {
    id: String(item.id || ''),
    country: formatCountryName(item.country),
    city: item.city || '',
    flagUrl: item.imageUrl || '',
    key: item.url || '',
  };
}

function formatCountryName(country) {
  const normalized = (country || '').trim();
  const normalizedWithoutYo = normalized.replaceAll('ё', 'е').replaceAll('Ё', 'Е');

  if (normalizedWithoutYo === 'Королевство Нидерландов') {
    return 'Нидерланды';
  }

  if (normalizedWithoutYo === 'Соединенные Штаты Америки') {
    return 'США';
  }

  return normalized || 'Неизвестно';
}

function formatKeysCount(count, isLoading, error) {
  if (isLoading) {
    return 'Загрузка';
  }

  if (error) {
    return 'Недоступно';
  }

  const remainder10 = count % 10;
  const remainder100 = count % 100;
  const suffix = remainder10 === 1 && remainder100 !== 11
    ? 'страна'
    : remainder10 >= 2 && remainder10 <= 4 && (remainder100 < 12 || remainder100 > 14)
      ? 'страны'
      : 'стран';

  return `${count} ${suffix}`;
}

function isCanceledRequest(error) {
  return error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
}

function getDownloadFileName(contentDisposition, fallbackFileName) {
  const header = contentDisposition || '';
  const utf8FileName = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FileName?.[1]) {
    return decodeURIComponent(utf8FileName[1].trim().replace(/^"|"$/g, ''));
  }

  const fileName = header.match(/filename="?([^";]+)"?/i);
  return fileName?.[1]?.trim() || fallbackFileName;
}

function SpeedIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M12 4a10 10 0 0 0-9.6 12.8 1 1 0 0 0 1 .7h17.2a1 1 0 0 0 1-.7A10 10 0 0 0 12 4Zm0 2a8 8 0 0 1 7.7 9.5H4.3A8 8 0 0 1 12 6Zm4.9 3.7-4.2 4.2a1.7 1.7 0 1 1-1.4-1.4l4.2-4.2 1.4 1.4Z" />
    </svg>
  );
}

function ChevronIcon({ isExpanded }) {
  return (
    <svg className={isExpanded ? 'chevron-icon chevron-icon_expanded' : 'chevron-icon'} aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="m6.7 9.3 5.3 5.3 5.3-5.3 1.4 1.4-6 6a1 1 0 0 1-1.4 0l-6-6 1.4-1.4Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M16.8 12.6c0-2.4 2-3.5 2.1-3.6-1.1-1.7-2.8-1.9-3.4-1.9-1.5-.2-2.8.8-3.6.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.8 2.4 3 2.3 1.2-.1 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.9-1.1-2.9-3.6ZM14.6 5.6c.7-.8 1.1-1.9 1-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 .1 2-.5 2.7-1.3Z" />
    </svg>
  );
}

function WindowsIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M3 5.1 10.6 4v7.3H3V5.1Zm8.6-1.3L21 2.5v8.8h-9.4V3.8ZM3 12.7h7.6V20L3 18.9v-6.2Zm8.6 0H21v8.8l-9.4-1.3v-7.5Z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M8 2h8a2.5 2.5 0 0 1 2.5 2.5v15A2.5 2.5 0 0 1 16 22H8a2.5 2.5 0 0 1-2.5-2.5v-15A2.5 2.5 0 0 1 8 2Zm0 2a.5.5 0 0 0-.5.5v15a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-15A.5.5 0 0 0 16 4H8Zm2.5 13.7h3v1.6h-3v-1.6Z" />
    </svg>
  );
}

export default VlessKeysPage;
